"use client";

import { useMemo, useState } from "react";
import type { Voice } from "@/lib/creatorVoice/api";
import VoiceCard from "@/components/creatorVoice/VoiceCard";

const TIER_SECTIONS: { key: "free" | "premium" | "pro"; heading: string }[] = [
  { key: "free", heading: "Free Voices" },
  { key: "premium", heading: "★ Premium Voices" },
  { key: "pro", heading: "🔒 Pro Voices" },
];

function tierOf(v: Voice): "free" | "premium" | "pro" {
  if (v.tier === "pro") return "pro";
  if (v.is_premium) return "premium";
  return "free";
}

export default function VoicePickerModal({
  voices,
  selectedId,
  onSelect,
  onClose,
}: {
  voices: Voice[];
  selectedId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return voices;
    return voices.filter(
      (v) => v.label.toLowerCase().includes(q) || v.category.toLowerCase().includes(q) || v.language_label.toLowerCase().includes(q)
    );
  }, [voices, search]);

  const sections = useMemo(() => {
    const hindi = filtered.filter((v) => v.language === "hi");
    const rest = filtered.filter((v) => v.language !== "hi");
    const tierSections = TIER_SECTIONS.map((section) => ({ ...section, voices: rest.filter((v) => tierOf(v) === section.key) }));
    return [...tierSections, { key: "hindi" as const, heading: "Hindi Voices", voices: hindi }].filter((s) => s.voices.length > 0);
  }, [filtered]);

  // Anchored absolutely within its (relative) parent — covers just the
  // Voice + Tone block it's triggered from, not the whole screen, per the
  // ElevenLabs-style "pick from right here" interaction.
  return (
    <div className="absolute inset-0 z-20 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-[#0f172a]">
      <div className="flex items-center justify-between border-b border-slate-200 p-3 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Choose a voice</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          ✕
        </button>
      </div>

      <div className="border-b border-slate-200 p-3 dark:border-slate-800">
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search voices…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-rose-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
        />
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-3">
        {sections.map((section) => (
          <div key={section.key}>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{section.heading}</p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {section.voices.map((v) => (
                <VoiceCard
                  key={v.id}
                  voice={v}
                  selected={v.id === selectedId}
                  onSelect={() => {
                    onSelect(v.id);
                    onClose();
                  }}
                />
              ))}
            </div>
          </div>
        ))}
        {sections.length === 0 ? <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">No voices match your search.</p> : null}
      </div>
    </div>
  );
}
